import { useId, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@arabquiz/ui'
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Download,
  FileSpreadsheet,
  Loader2,
  Upload,
  X,
  XCircle,
} from 'lucide-react'
import {
  adminKeys,
  formatBytes,
  type AdminBankCategory,
  type ImportResult,
} from './types'

const MAX_BYTES = 5 * 1024 * 1024
const ACCEPTED = ['.csv', '.xlsx']

/** Canonical English template headers (matched case-insensitively by the parser). */
const TEMPLATE_HEADERS = [
  'type', 'text', 'time_seconds', 'category',
  'option1', 'option2', 'option3', 'option4', 'option5', 'option6',
  'correct_option', 'correct_answer',
  'item1', 'item2', 'item3', 'item4', 'item5', 'item6', 'item7', 'item8',
]

const TEMPLATE_ROWS = [
  ['MultipleChoice', 'ما هي عاصمة مصر؟', '20', 'جغرافيا', 'القاهرة', 'الإسكندرية', 'أسوان', 'الجيزة', '', '', '1', '', '', '', '', '', '', '', '', ''],
  ['TrueFalse', 'الأرض كروية الشكل', '15', 'علوم', '', '', '', '', '', '', '', 'true', '', '', '', '', '', '', '', ''],
]

type ImportOutcome = {
  httpOk: boolean
  result: ImportResult
}

/**
 * CSV/Excel bulk-import panel for the admin Question Bank. Provides a
 * keyboard-operable dropzone (drag-or-click), a client-side template download
 * (canonical English headers + Arabic example rows), an optional target
 * category, and a partial-success result panel: a colour+icon summary line
 * (`aria-live="polite"`) plus a collapsible per-row error list. Client
 * validation rejects the wrong extension / oversized files before upload.
 * On a successful (incl. partial) import the admin bank list and the overview
 * analytics are invalidated.
 */
export function AdminImportPanel() {
  const { t } = useTranslation('admin')
  const { t: tQuiz } = useTranslation('quiz')
  const queryClient = useQueryClient()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const categorySelectId = useId()

  const [file, setFile] = useState<File | null>(null)
  const [categoryId, setCategoryId] = useState('')
  const [clientError, setClientError] = useState<string | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [errorsOpen, setErrorsOpen] = useState(true)

  // Map a stable import error code to its localized message (quiz namespace
  // `import.*`). Uses literal keys (not a dynamic `t(\`import.${code}\`)`) so
  // the typed `t` checks every key at compile time.
  function codeLabel(code: string): string {
    switch (code) {
      case 'unknownType': return tQuiz('import.unknownType')
      case 'missingText': return tQuiz('import.missingText')
      case 'textTooLong': return tQuiz('import.textTooLong')
      case 'tooFewOptions': return tQuiz('import.tooFewOptions')
      case 'optionTooLong': return tQuiz('import.optionTooLong')
      case 'invalidCorrectOption': return tQuiz('import.invalidCorrectOption')
      case 'missingCorrectAnswer': return tQuiz('import.missingCorrectAnswer')
      case 'tooFewItems': return tQuiz('import.tooFewItems')
      case 'tooManyItems': return tQuiz('import.tooManyItems')
      case 'itemTooLong': return tQuiz('import.itemTooLong')
      case 'invalidTime': return tQuiz('import.invalidTime')
      case 'categoryNotResolvable': return tQuiz('import.categoryNotResolvable')
      case 'fileUnreadable': return tQuiz('import.fileUnreadable')
      case 'fileTooLarge': return tQuiz('import.fileTooLarge')
      case 'missingFile': return tQuiz('import.missingFile')
      case 'unknownExtension': return tQuiz('import.unknownExtension')
      case 'emptyFile': return tQuiz('import.emptyFile')
      case 'missingHeader': return tQuiz('import.missingHeader')
      default: return tQuiz('import.unknown')
    }
  }

  const categoriesQuery = useQuery({
    queryKey: ['questionBank', 'categories'],
    queryFn: async ({ signal }) => {
      const res = await fetch('/api/v1/question-bank/categories', {
        credentials: 'include',
        signal,
      })
      if (!res.ok) {
        throw new Error(`Failed to load categories: ${res.status}`)
      }
      return (await res.json()) as AdminBankCategory[]
    },
  })

  const importMutation = useMutation({
    mutationFn: async (payload: { file: File; categoryId: string }): Promise<ImportOutcome> => {
      const form = new FormData()
      form.append('file', payload.file)
      if (payload.categoryId) {
        form.append('categoryId', payload.categoryId)
      }
      const res = await fetch('/api/v1/admin/question-bank/import', {
        method: 'POST',
        credentials: 'include',
        body: form,
      })
      // The body is an ImportResultDto on both 200 (incl. partial) and 400
      // (a fundamentally unreadable file → a single file-level error code).
      let result: ImportResult | null = null
      try {
        result = (await res.json()) as ImportResult
      } catch {
        result = null
      }
      if (!result) {
        throw new Error(`Import failed: ${res.status}`)
      }
      return { httpOk: res.ok, result }
    },
    onSuccess: (outcome) => {
      setErrorsOpen(true)
      if (outcome.httpOk) {
        void queryClient.invalidateQueries({ queryKey: adminKeys.bankRoot })
        void queryClient.invalidateQueries({ queryKey: adminKeys.analytics })
      }
    },
  })

  function validateAndSet(next: File | null) {
    setClientError(null)
    importMutation.reset()
    if (!next) {
      setFile(null)
      return
    }
    const lower = next.name.toLowerCase()
    if (!ACCEPTED.some((ext) => lower.endsWith(ext))) {
      setClientError(t('importWrongType'))
      setFile(null)
      return
    }
    if (next.size > MAX_BYTES) {
      setClientError(t('importTooLarge'))
      setFile(null)
      return
    }
    setFile(next)
  }

  function downloadTemplate() {
    const rows = [TEMPLATE_HEADERS, ...TEMPLATE_ROWS]
    const csv = rows
      .map((row) => row.map(csvCell).join(','))
      .join('\r\n')
    // UTF-8 BOM so Excel opens the Arabic example rows correctly.
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'question-bank-template.csv'
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const dropped = e.dataTransfer.files?.[0] ?? null
    validateAndSet(dropped)
  }

  const outcome = importMutation.data
  const summaryKind: 'success' | 'partial' | 'failure' | null = outcome
    ? !outcome.httpOk
      ? 'failure'
      : outcome.result.failed > 0
        ? 'partial'
        : 'success'
    : null

  return (
    <section
      aria-label={t('importTitle')}
      className="rounded-2xl border border-brand-100 bg-white border-s-4 border-s-brand-500 p-6 shadow-sm"
    >
      <div className="flex items-center justify-between gap-4">
        <h2 className="text-lg font-bold font-display text-brand-900 tracking-tight">
          {t('importTitle')}
        </h2>
        <Button
          type="button"
          variant="outline"
          onClick={downloadTemplate}
          className="gap-2"
        >
          <Download className="h-4 w-4" aria-hidden="true" />
          <span>{t('importTemplate')}</span>
        </Button>
      </div>

      {/* Dropzone — the label makes the visually-hidden file input keyboard
          operable and clickable; drag handlers provide the drag fallback. */}
      <label
        htmlFor={inputId}
        onDragOver={(e) => {
          e.preventDefault()
          setDragOver(true)
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        className={
          'mt-5 flex cursor-pointer flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed px-6 py-10 text-center transition-colors focus-within:ring-2 focus-within:ring-brand-500 ' +
          (dragOver
            ? 'border-brand-400 bg-brand-50/60'
            : 'border-brand-200 hover:border-brand-300 hover:bg-brand-50/30')
        }
      >
        <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-50 text-brand-600">
          <Upload className="h-6 w-6" aria-hidden="true" />
        </span>
        <span className="text-sm font-semibold text-brand-800">{t('importDropzone')}</span>
        <span className="text-xs text-brand-500">{t('importDropzoneHint')}</span>
        <input
          ref={inputRef}
          id={inputId}
          type="file"
          accept=".csv,.xlsx"
          className="sr-only"
          onChange={(e) => validateAndSet(e.target.files?.[0] ?? null)}
        />
      </label>

      {clientError ? (
        <p className="mt-3 text-sm text-red-600" role="alert">
          {clientError}
        </p>
      ) : null}

      {/* Selected file. */}
      {file ? (
        <div className="mt-4 flex items-center justify-between gap-3 rounded-xl border border-brand-100 bg-brand-50/40 px-4 py-3">
          <span className="flex min-w-0 items-center gap-2 text-sm text-brand-800">
            <FileSpreadsheet className="h-4 w-4 shrink-0 text-brand-500" aria-hidden="true" />
            <span className="truncate">
              {t('importFileSelected', { name: file.name, size: formatBytes(file.size) })}
            </span>
          </span>
          <button
            type="button"
            onClick={() => {
              validateAndSet(null)
              if (inputRef.current) inputRef.current.value = ''
            }}
            aria-label={t('importRemoveFile')}
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-brand-600 hover:bg-brand-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>
      ) : null}

      {/* Target category + import action. */}
      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="w-full sm:max-w-xs">
          <label htmlFor={categorySelectId} className="block text-sm font-semibold text-brand-900 mb-1.5">
            {t('importTargetCategory')}
          </label>
          <select
            id={categorySelectId}
            value={categoryId}
            onChange={(e) => setCategoryId(e.target.value)}
            className="h-10 w-full rounded-md border border-brand-200 bg-white ps-3 pe-8 text-sm text-brand-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <option value="">{t('importNoCategory')}</option>
            {(categoriesQuery.data ?? []).map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </div>

        <Button
          type="button"
          onClick={() => {
            if (file) importMutation.mutate({ file, categoryId })
          }}
          disabled={!file || importMutation.isPending}
          className="gap-2 bg-gradient-to-bl from-brand-500 to-brand-600 hover:from-brand-600 hover:to-brand-700 shadow-md hover:shadow-lg"
        >
          {importMutation.isPending ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              <span>{t('importing')}</span>
            </>
          ) : (
            <>
              <Upload className="h-4 w-4" aria-hidden="true" />
              <span>{t('importButton')}</span>
            </>
          )}
        </Button>
      </div>

      {/* Result summary (aria-live) + collapsible per-row errors. */}
      <div aria-live="polite" className="mt-5">
        {importMutation.isError ? (
          <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>{tQuiz('import.fileUnreadable')}</span>
          </div>
        ) : null}
        {outcome && summaryKind ? (
          <ImportSummary
            kind={summaryKind}
            summaryText={t('importSummary', {
              imported: outcome.result.imported,
              totalRows: outcome.result.totalRows,
            })}
            failedText={t('importFailedCount', { failed: outcome.result.failed })}
            allSucceededText={t('importAllSucceeded')}
            fileErrorText={
              outcome.result.errors[0]
                ? codeLabel(outcome.result.errors[0].code)
                : tQuiz('import.unknown')
            }
          />
        ) : null}
      </div>

      {outcome && summaryKind === 'partial' && outcome.result.errors.length > 0 ? (
        <div className="mt-3 rounded-xl border border-amber-200 bg-amber-50/60">
          <button
            type="button"
            onClick={() => setErrorsOpen((open) => !open)}
            aria-expanded={errorsOpen}
            className="flex w-full items-center justify-between gap-2 px-4 py-3 text-sm font-semibold text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500"
          >
            <span>{t('importErrorsTitle')}</span>
            <ChevronDown
              className={'h-4 w-4 transition-transform ' + (errorsOpen ? 'rotate-180' : '')}
              aria-hidden="true"
            />
          </button>
          {errorsOpen ? (
            <ul className="border-t border-amber-200 px-4 py-3 space-y-1.5 text-sm text-amber-900">
              {outcome.result.errors.map((error, i) => {
                const codeText = codeLabel(error.code)
                return (
                  <li key={`${error.rowNumber}-${i}`} className="flex gap-1.5">
                    <span className="font-semibold tabular-nums">
                      {t('importRowLabel', { row: error.rowNumber })}:
                    </span>
                    <span>
                      {codeText}
                      {error.detail ? ` (${error.detail})` : ''}
                    </span>
                  </li>
                )
              })}
            </ul>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

type ImportSummaryProps = {
  kind: 'success' | 'partial' | 'failure'
  summaryText: string
  failedText: string
  allSucceededText: string
  fileErrorText: string
}

function ImportSummary({
  kind,
  summaryText,
  failedText,
  allSucceededText,
  fileErrorText,
}: ImportSummaryProps) {
  if (kind === 'failure') {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{fileErrorText}</span>
      </div>
    )
  }
  if (kind === 'success') {
    return (
      <div className="flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          {summaryText} — {allSucceededText}
        </span>
      </div>
    )
  }
  return (
    <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <span>
        {summaryText} <span className="font-semibold">{failedText}</span>
      </span>
    </div>
  )
}

/** Minimal CSV cell escaping for the client-side template. */
function csvCell(value: string): string {
  if (/[",\r\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}
